// import Avatar from 'boring-avatars'
import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'

import ArrowDownIcon from '@/assets/icons/arrow-down.svg?react'
import EyeClose from '@/assets/icons/eye-close.svg?react'
import EyeOpen from '@/assets/icons/eye-open.svg?react'
import LogoImage from '@/assets/images/logo-gata.svg?react'
import { Trans } from '@/components'
import { Button } from '@/components/base'
import { EduVerify } from '@/models/profile.ts'
import { useUserStore } from '@/stores/user'
import { formatDurationToHours, formatPrice, formatUserName } from '@/utils/formatter.ts'

import { AnnouncementBanner } from './AnnouncementBanner'
import { UserAvatar } from './UserAvatar'
import { UserDropdown } from './UserDropdown'

interface HeaderProps {
  setExchargeModalOpen: (open: boolean) => void
  eduVerify?: EduVerify
}

export const Header = ({ setExchargeModalOpen, eduVerify }: HeaderProps) => {
  const userStore = useUserStore()

  const [isBalanceHidden, setIsBalanceHidden] = useState(false)
  // const [showDiscordTooltip, setShowDiscordTooltip] = useState(false)

  const toggleBalanceVisibility = () => {
    setIsBalanceHidden(!isBalanceHidden)
  }

  useEffect(() => {
    if (Cookies.get('is_balance_hidden')) {
      setIsBalanceHidden(Cookies.get('is_balance_hidden') === 'true')
    }
  }, [])

  useEffect(() => {
    Cookies.set('is_balance_hidden', String(isBalanceHidden))
  }, [isBalanceHidden])

  return (
    <header className="px-8 py-[18px] sticky top-0 z-[50]">
      <div className="flex items-center justify-between min-w-[1024px]">
        <div className="w-fit flex flex-row gap-4 items-center">
          <Link to="/" replace>
            <LogoImage width={140} height={32} />
          </Link>
          <div className="h-5 border-r border-r-[#E1E4EA]"></div>
          <div className="w-80 h5 text-primary">DeCloud</div>
        </div>

        <div className="w-full min-w-0 flex justify-center" />
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-full flex justify-center">
          <AnnouncementBanner show={false} />
        </div>

        <div className="flex items-center gap-x-4 w-auto justify-end">
          <div className="dropdown group relative text-nowrap">
            <div className="t-sb py-2 flex space-x-2 items-center">
              <div>
                {isBalanceHidden
                  ? '∗∗∗∗∗∗'
                  : formatPrice(
                      userStore.account.totalBonus.moneyCent ?? 0,
                      false,
                      userStore.account.totalBonus.moneyCent > 100 ? 'Credits' : 'Credit',
                    )}
              </div>
              <div className="cursor-pointer text-secondary" onClick={toggleBalanceVisibility}>
                {isBalanceHidden ? (
                  <EyeClose className="w-4 h-4" />
                ) : (
                  <EyeOpen className="w-4 h-4" />
                )}
              </div>
            </div>
            <ul className="w-[280px] absolute -left-1/2 top-full bg-white shadow-card-100 rounded-2xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transform transition-all duration-300">
              <li className="w-full flex items-center justify-between mb-2 py-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="header.creditInfo.balance">Balance (Credit)</Trans>
                </div>
                <div className="t-sb text-primary">
                  {`${formatPrice(userStore.account.totalBonus.moneyCent ?? 0, false, '')}`}
                </div>
              </li>
              <li className="w-full flex items-center justify-between mb-2 py-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="header.creditInfo.totalSpending">Total Spending (Credit)</Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatPrice(userStore.account.expense.amount ?? 0, false, '')}
                </div>
              </li>
              <li className="w-full flex items-center justify-between mb-2 py-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="header.creditInfo.duration">Total Duration (h)</Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatDurationToHours(userStore.account.expense.timeSec ?? 0)}
                </div>
              </li>
              <li className="w-full flex items-center justify-between mb-2">
                <NavLink to="/billing" className="w-full">
                  <Button className="w-full t-x-sb py-2">
                    <Trans i18nKey="header.creditInfo.mySpendingBtn">My Spending</Trans>
                  </Button>
                </NavLink>
              </li>
              <li className="w-full flex items-center justify-between mb-2">
                <Button
                  type="secondary"
                  className="w-full t-x-sb py-2"
                  onClick={() => setExchargeModalOpen(true)}
                >
                  <Trans i18nKey="header.creditInfo.rechargeBtn">Recharge</Trans>
                </Button>
              </li>
            </ul>
          </div>

          <div className="h-5 border-r border-r-[#E1E4EA]"></div>
          <div className="relative group">
            <div className="flex items-center space-x-2 cursor-pointer group-hover:bg-neutral-50 rounded-lg p-1">
              {/* <Avatar size={32} name={userStore.name} variant="beam" colors={['#525866']} /> */}
              <UserAvatar size={32} />
              <span className="t-sb">{formatUserName(userStore.name)}</span>
              <ArrowDownIcon className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
            </div>
            <UserDropdown eduVerify={eduVerify} />
          </div>
        </div>
      </div>
    </header>
  )
}
